import { Download, FileText, ImageIcon } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { downloadAttachment } from '../../utilities/DownloadAttachment';
import { useRenewAttachmentMutation } from '../../store/api/chatApi';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';

interface ChatAttachment {
	key: string;
	fileName?: string | null;
	mimeType?: string;
}

const isImage = (attachment: ChatAttachment) => attachment.mimeType?.startsWith('image/');
export const isPdf = (attachment: ChatAttachment) =>
	attachment.mimeType === 'application/pdf' || attachment.fileName?.toLowerCase().endsWith('.pdf') === true;

export function ChatAttachmentPreview({ attachment }: { attachment: ChatAttachment }) {
	const [isPreviewOpen, setIsPreviewOpen] = useState(false);
	const [previewUrl, setPreviewUrl] = useState('');
	const [renewAttachment] = useRenewAttachmentMutation();
	const previewable = isImage(attachment) || isPdf(attachment);
	const fileName = attachment.fileName?.trim() || 'Attachment';
	const renewPreviewUrl = useCallback(async () => {
		try {
			const renewedAttachment = await renewAttachment({ key: attachment.key, fileName }).unwrap();
			setPreviewUrl(renewedAttachment.key);
		} catch {
			setPreviewUrl('');
		}
	}, [attachment.key, fileName, renewAttachment]);

	useEffect(() => {
		if (previewable) void renewPreviewUrl();
	}, [previewable, renewPreviewUrl]);

	return (
		<>
			<div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
				{isImage(attachment) && previewUrl && (
					<button
						aria-label={`Preview ${fileName}`}
						className="block w-full cursor-zoom-in bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
						onClick={() => setIsPreviewOpen(true)}
						type="button"
					>
						<img alt={fileName} className="max-h-72 w-full object-cover" src={previewUrl} onError={renewPreviewUrl} />
					</button>
				)}
				<div className="flex min-w-0 items-center gap-2 p-2.5">
					{!isImage(attachment) && (
						<button
							aria-label={previewable ? `Preview ${fileName}` : undefined}
							className={`flex size-10 shrink-0 items-center justify-center rounded-lg ${previewable ? 'cursor-pointer bg-destructive/10 text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring' : 'cursor-default bg-slate-100 text-slate-500'}`}
							onClick={() => previewable && previewUrl && setIsPreviewOpen(true)}
							type="button"
						>
							{isPdf(attachment) ? <FileText className="size-5" /> : <ImageIcon className="size-5" />}
						</button>
					)}
					<button
						aria-label={previewable ? `Preview ${fileName}` : undefined}
						className={`min-w-0 flex-1 text-left text-sm font-medium text-slate-800 ${previewable ? 'cursor-pointer hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring' : 'cursor-default'}`}
						onClick={() => previewable && previewUrl && setIsPreviewOpen(true)}
						type="button"
					>
						<span className="block truncate">{fileName}</span>
						<span className="block pt-0.5 text-xs font-normal text-slate-500">
							{isImage(attachment) ? 'Image preview' : isPdf(attachment) ? 'PDF preview' : 'Attachment'}
						</span>
					</button>
					<Button aria-label={`Download ${fileName}`} onClick={() => downloadAttachment({ ...attachment, fileName })} size="icon-sm" type="button" variant="ghost">
						<Download className="size-4" />
					</Button>
				</div>
			</div>
			{previewable && previewUrl && (
				<Dialog onOpenChange={setIsPreviewOpen} open={isPreviewOpen}>
					<DialogContent className="max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] max-w-5xl gap-3 overflow-hidden p-3 sm:p-4">
						<DialogHeader className="min-w-0 pr-8">
							<DialogTitle className="truncate">{fileName}</DialogTitle>
							<DialogDescription>{isImage(attachment) ? 'Image preview' : 'PDF preview'}</DialogDescription>
						</DialogHeader>
						{isImage(attachment) ? (
							<img alt={fileName} className="max-h-[calc(100dvh-10rem)] w-full rounded-lg object-contain" src={previewUrl} onError={renewPreviewUrl} />
						) : (
							<iframe className="h-[calc(100dvh-11rem)] w-full rounded-lg border border-slate-200" src={previewUrl} title={`Preview of ${fileName}`} />
						)}
					</DialogContent>
				</Dialog>
			)}
		</>
	);
}
